import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsBoolean,
	IsDateString,
	IsIn,
	IsInt,
	IsObject,
	IsOptional,
	IsString,
	Matches,
	Max,
	MaxLength,
	Min,
	ValidateNested,
} from 'class-validator';
import {
	CAMPAIGN_CONTENT_KINDS,
	CAMPAIGN_KINDS,
	CAMPAIGN_NO_LINKEDIN_RULES,
	CAMPAIGN_NO_PHONE_RULES,
	CAMPAIGN_STEP_TYPES,
	CAMPAIGN_WAIT_UNITS,
} from '../../constants/campaign-sequence';
import { CampaignPeopleRulesDto } from './campaign-builder.dto';
import type { CampaignContentEntity } from '../entity/campaign-content.entity';
import type { CampaignStepEntity } from '../entity/campaign-step.entity';
import type { CampaignEntity } from '../entity/campaign.entity';

/** PD-17613 / PD-17614 — the campaign's sequence, copy and run, over the API. */

/** Most steps a sequence may have. */
export const MAX_CAMPAIGN_STEPS = 30;

export class CampaignStepInputDto {
	/**
	 * PD-17617 — the step this row was before the save (its id), so its copy
	 * moves with it when steps are reordered. Omitted for a new step.
	 */
	@IsOptional()
	@IsInt()
	@Min(1)
	public id?: number;

	@IsIn(CAMPAIGN_STEP_TYPES as unknown as Array<string>)
	public type!: string;

	@IsInt()
	@Min(0)
	@Max(365)
	public waitAmount!: number;

	@IsIn(CAMPAIGN_WAIT_UNITS as unknown as Array<string>)
	public waitUnit!: string;

	@IsOptional()
	@IsObject()
	public settings?: Record<string, unknown>;
}

/** A new draft campaign (the builder's first save). */
export class CreateSalesCampaignDto {
	@IsString()
	@MaxLength(255)
	public name!: string;

	@IsOptional()
	@IsIn(CAMPAIGN_KINDS as unknown as Array<string>)
	public kind?: string | null;
}

/** Replace a draft campaign's steps (the sequence editor saves the whole list). */
export class ReplaceCampaignStepsDto {
	@IsArray()
	@ArrayMaxSize(MAX_CAMPAIGN_STEPS)
	@ValidateNested({ each: true })
	@Type(() => CampaignStepInputDto)
	public steps!: Array<CampaignStepInputDto>;

	/**
	 * PD-17625 — steps were added after the end of a running campaign: people
	 * who finished without a reply carry on from the first new step.
	 */
	@IsOptional()
	@IsBoolean()
	public continueFinished?: boolean;
}

/** Save one piece of copy (creates or updates; a wording change clears its approval). */
export class SaveCampaignContentDto {
	@IsOptional()
	@IsInt()
	@Min(1)
	public stepNo?: number | null;

	@IsIn(CAMPAIGN_CONTENT_KINDS as unknown as Array<string>)
	public kind!: string;

	@IsOptional()
	@IsString()
	@MaxLength(500)
	public subjectA?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(500)
	public subjectB?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(20000)
	public body?: string | null;
}

/** campaign.providerSettings over the API. */
export class CampaignProviderSettingsDto {
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(20)
	@IsString({ each: true })
	public senderTagIds?: Array<string>;

	@IsOptional()
	@IsString()
	@MaxLength(64)
	public timezone?: string;

	@IsOptional()
	@Matches(/^([01]\d|2[0-3]):[0-5]\d$/)
	public sendFrom?: string;

	@IsOptional()
	@Matches(/^([01]\d|2[0-3]):[0-5]\d$/)
	public sendTo?: string;

	@IsOptional()
	@IsArray()
	@IsInt({ each: true })
	@Min(0, { each: true })
	@Max(6, { each: true })
	public sendDays?: Array<number>;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(10000)
	public dailyNewPeople?: number;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(1000)
	public mailboxDailyCap?: number;

	@IsOptional()
	@IsBoolean()
	public openTracking?: boolean;

	@IsOptional()
	@IsBoolean()
	public linkTracking?: boolean;
}

/** The brief and sending setup (the builder's About and Senders steps). */
export class UpdateCampaignSetupDto {
	@IsOptional()
	@IsString()
	@MaxLength(255)
	public name?: string;

	@IsOptional()
	@IsIn(CAMPAIGN_KINDS as unknown as Array<string>)
	public kind?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public purpose?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public ask?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public valueStatement?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public incentive?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public whoTheyAre?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public voice?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(5000)
	public neverSay?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(20000)
	public writingInstructions?: string | null;

	@IsOptional()
	@IsIn(CAMPAIGN_NO_PHONE_RULES as unknown as Array<string>)
	public noPhoneRule?: string | null;

	@IsOptional()
	@IsIn(CAMPAIGN_NO_LINKEDIN_RULES as unknown as Array<string>)
	public noLinkedInRule?: string | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => CampaignProviderSettingsDto)
	public providerSettings?: CampaignProviderSettingsDto;

	/** PD-17616 — the People step's rules, kept on campaign.settings.peopleRules. */
	@IsOptional()
	@ValidateNested()
	@Type(() => CampaignPeopleRulesDto)
	public peopleRules?: CampaignPeopleRulesDto;
}

/** "Mark called" on a person's call step, or the softphone's wrap-up. */
export class LogCampaignCallDto {
	/** The call outcome (a Call Disposition value's name). */
	@IsOptional()
	@IsString()
	@MaxLength(100)
	public dispositionCode?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(10000)
	public note?: string | null;

	/** When the outcome means a call-back. */
	@IsOptional()
	@IsDateString()
	public callbackAt?: string | null;
}

/** Done on a LinkedIn, voicemail drop or task step. */
export class CompleteCampaignStepDto {
	@IsOptional()
	@IsString()
	@MaxLength(100)
	public outcome?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(10000)
	public note?: string | null;
}

/** A call-back at a set time. */
export class ScheduleCampaignCallbackDto {
	@IsDateString()
	public at!: string;

	@IsOptional()
	@IsString()
	@MaxLength(10000)
	public note?: string | null;
}

/** GET a campaign's sequence: the campaign, its steps and its copy. */
export interface CampaignSequenceDto {
	campaign: CampaignEntity;
	steps: Array<CampaignStepEntity>;
	contents: Array<CampaignContentEntity>;
}
